import { useEffect, useRef, useState } from 'react';
import { useCancelTask } from '@/state/data/task';
import { clinicCallOf, clinicCallLabel, notThisPatientNote, callCancelCopy } from '@/logic/task';

const when = (iso) => {
  const t = Date.parse(iso);
  return Number.isFinite(t)
    ? new Date(t).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    : null;
};

// Cancels a clinic-line call as not this patient (an outgoing one: did not reach the patient). WF17 refuses that cancel
// without a reason, hence the required field.
export default function NotThisPatientModal({ task, withingsUserId, actor, onClose }) {
  const call = clinicCallOf(task);
  const copy = callCancelCopy(call);
  const [reason, setReason] = useState('');
  const cancel = useCancelTask();
  // Every way out waits for the cancel: closing mid-flight would drop its follow-up while the cancel still lands.
  const pendingRef = useRef(false);
  pendingRef.current = cancel.isPending;
  const dismiss = () => { if (!pendingRef.current) onClose(); };

  // Escape + body scroll lock. CAPTURE + stopPropagation: the patient drawer has its own Escape listener.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      if (!pendingRef.current) onClose();
    };
    window.addEventListener('keydown', onKey, true);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey, true); document.body.style.overflow = prev; };
  }, [onClose]);

  const note = notThisPatientNote(reason, call);
  const canSubmit = !!note && !cancel.isPending;
  const submit = () => {
    if (!canSubmit) return;
    cancel.mutate(
      { taskId: task.task_id, notes: note, withingsUserId, actor },
      { onSuccess: () => onClose({ note }) },
    );
  };

  return (
    <div className="modal-overlay" onClick={dismiss}>
      <div
        className="modal-content"
        role="dialog"
        aria-modal="true"
        aria-labelledby="not-this-patient-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="modal-header">
          <h2 className="header3" id="not-this-patient-title">{copy.action}</h2>
          <button className="modal-close" aria-label="Close" onClick={dismiss} disabled={cancel.isPending}>×</button>
        </header>

        <div className="modal-body">
          <div style={{ margin: '0 0 16px' }}>
            <div className="header4" style={{ margin: 0 }}>{call ? clinicCallLabel(call) : task?.title}</div>
            {call && when(call.at) && (
              <div className="detail1 text-secondary" style={{ marginTop: 2 }}>{when(call.at)}</div>
            )}
          </div>

          <p className="body2 text-secondary" style={{ margin: '0 0 16px' }}>
            The call stops counting as this patient&apos;s care and stays in their history as cancelled. Its line in
            the conversation, and Spruce&apos;s summary if one was copied, stay on the patient&apos;s record. A monthly
            claim already created for its month is not changed: re-check it before filing.
            {copy.moveNote && ` ${copy.moveNote}`}
          </p>

          <div className="rx-field">
            <label className="eyebrow" htmlFor="not-this-patient-reason">Reason (required)</label>
            <textarea
              id="not-this-patient-reason"
              className="rx-input"
              rows={2}
              maxLength={300}
              autoFocus
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={copy.placeholder}
            />
            <p className="detail1" style={{ color: 'var(--wi-text-secondary)', margin: '4px 0 0' }}>
              {copy.hint}
            </p>
          </div>

          {cancel.error && <div className="error-banner">{String(cancel.error)}</div>}
        </div>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button className="btn-secondary" onClick={dismiss} disabled={cancel.isPending}>Keep the call</button>
          <button className="btn-primary" onClick={submit} disabled={!canSubmit}>
            {cancel.isPending ? 'Removing…' : 'Remove the call'}
          </button>
        </div>
      </div>
    </div>
  );
}
